import { useEffect, useState } from 'react';
import { IconUser, IconLogout, IconChevronDown, IconSettings, IconSparkles, IconShoppingCart, IconReceipt, IconLayoutDashboard, IconTemplate, IconPrinter, IconPhoto, IconWand, IconTags, IconCategory, IconCheck, IconPlus, IconKeyboard, IconLanguage, IconFileText, IconCoins, IconSun, IconMoon, IconApps, IconTrophy } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { notifications } from '@/lib/notifications';
import usageService from '../../services/usageService';
import { buyCreditPathFor } from '../../store/paymentConfigStore';
import { creditParts } from '../../utils/credit';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import UserShortcutHelpModal from './UserShortcutHelpModal';
import GlossShine from './GlossShine';
import OfficeSpaceDialogs from './OfficeSpaceDialogs';
import ProfileCompletionBanner, { getProfileCompletion } from './ProfileCompletionBanner';

export default function UserMenu({ compact = false }) {
    const navigate = useNavigate();
    const location = useLocation();
    const isOnboarding = location.pathname === '/onboard';
    const { user, logout } = useAuthStore();
    const { t, currentLanguage, changeLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const { officeSpaces, selectedOfficeSpace, selectOfficeSpace } = useOfficeSpaceStore();
    const [creditConfig, setCreditConfig] = useState(null);

    // Theme toggle (mirrors ColorSchemeToggle, now surfaced inside this menu)
    const [isDark, setIsDark] = useState(() =>
        localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && document.documentElement.classList.contains('dark'))
    );
    useEffect(() => {
        const root = document.documentElement;
        if (isDark) {
            root.classList.add('dark');
            root.setAttribute('data-mantine-color-scheme', 'dark');
            localStorage.setItem('theme', 'dark');
        } else {
            root.classList.remove('dark');
            root.setAttribute('data-mantine-color-scheme', 'light');
            localStorage.setItem('theme', 'light');
        }
    }, [isDark]);

    // Lifted dialogs/modals — rendered outside the dropdown so they survive its close
    const [shortcutsOpen, setShortcutsOpen]     = useState(false);
    const [officeCreateOpen, setOfficeCreateOpen] = useState(false);
    const [officeLeaveOpen, setOfficeLeaveOpen]   = useState(false);
    const [officeToLeave, setOfficeToLeave]       = useState(null);

    useEffect(() => {
        if (user?.role === 'OWNER') {
            usageService.getCreditConfig().then(setCreditConfig).catch(() => {});
        }
    }, [user?.role]);

    const showBuyCredit = user?.role === 'OWNER' && (
        creditConfig?.globalShowBuyCreditTab === true ||
        (user?.credit ?? Infinity) < 10 ||
        user?.settings?.showBuyCreditTab === true
    );

    // Buy-credit CTA points at the active gateway's tab (default SSLCommerz until config loads).
    const buyCreditPath = buyCreditPathFor(creditConfig?.activePaymentGateway, user?.country);

    const showOffice = !!user
        && (user.role === 'OWNER' || user.role === 'OPERATOR')
        && officeSpaces.length > 0;

    // Operators spend the owner's credit, so credit + profile-bonus UI is hidden for them.
    const isOperator = user?.role === 'OPERATOR';

    // Dismissible profile-completion nudge. Kept in component state only, so closing
    // it sticks across dropdown re-opens (UserMenu stays mounted) but the nudge
    // reappears on a fresh page load.
    const [profileNudgeClosed, setProfileNudgeClosed] = useState(false);
    const closeProfileNudge = () => setProfileNudgeClosed(true);

    // Clear the legacy persisted flag so users who dismissed it before see it again.
    useEffect(() => {
        try { localStorage.removeItem('profileNudgeClosed'); } catch { /* ignore */ }
    }, []);

    const showProfileCompletion = !isOnboarding && !isOperator && !profileNudgeClosed && getProfileCompletion(user).incomplete;

    const handleSelectOffice = (os) => {
        selectOfficeSpace(os);
        notifications.show({ title: t('officeSpace.officeSpaceSwitched'), message: t('officeSpace.switchedTo', { name: os.name }), color: 'blue' });
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleProfile = () => {
        if (user?.role === 'admin') {
            navigate('/admin/profile');
        } else {
            navigate('/profile');
        }
    };

    const handleSettings = () => {
        // Admin can't enter the user-only /settings route, so route admins to
        // their own settings page.
        navigate(user?.role === 'admin' ? '/admin/settings' : '/settings?tab=general');
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Credit shown as a tiny floating chip on the avatar (hidden for admins).
    const showCredit = user && user.role !== 'admin' && !isOperator && user.credit != null;
    const lowCredit = (user?.credit ?? 0) < 10;
    const fmtCredit = (n) => isBn
        ? String(n ?? 0).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d])
        : (n ?? 0).toLocaleString();
    // While un-migrated with profile credit, show both old • new (numbers only — the header
    // chip is too small for labels); otherwise just the wallet balance.
    const { showBoth: showBothCredit, showLegacyOnly, legacy: oldCredit, wallet: newCredit } = creditParts(user);
    const creditText = showBothCredit
        ? `${fmtCredit(oldCredit)} • ${fmtCredit(newCredit)}`
        : showLegacyOnly
        ? fmtCredit(oldCredit)
        : fmtCredit(newCredit);

    // Shared avatar element
    const Avatar = ({ size = 'md', withCredit = false }) => {
        const sizeClasses = size === 'sm'
            ? 'w-7 h-7 text-xs'
            : 'w-9 h-9 text-sm';

        const inner = user?.profilePicture ? (
            <img
                src={user.profilePicture}
                alt={user?.name || 'User'}
                className={cn('rounded-full object-cover shrink-0', sizeClasses)}
            />
        ) : (
            <div className={cn('rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold shrink-0', sizeClasses)}>
                {getInitials(user?.name)}
            </div>
        );

        if (!withCredit || !showCredit) return inner;

        return (
            <span className="relative inline-flex shrink-0">
                {inner}
                <span
                    title={t('profile.creditStatsLabel') || 'ক্রেডিট'}
                    className={cn(
                        'absolute -bottom-1.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-0.5 px-1 rounded-full text-[9px] font-bold leading-[1.5] border border-white dark:border-zinc-900 shadow-sm whitespace-nowrap',
                        lowCredit ? 'bg-red-500 text-white' : 'bg-primary text-white'
                    )}
                >
                    <IconCoins size={8} className="flex-shrink-0" />
                    {creditText}
                </span>
            </span>
        );
    };

    return (
        <>
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                {compact ? (
                    <button
                        type="button"
                        className="flex items-center focus:outline-none"
                    >
                        <Avatar size="sm" withCredit />
                    </button>
                ) : (
                    <button
                        type="button"
                        className="flex items-center gap-2.5 pl-1 pr-2.5 py-1 rounded-[9px] border border-zinc-200 dark:border-zinc-700 cursor-pointer transition-colors duration-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 focus:outline-none font-hind"
                    >
                        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-amber-400 text-white flex items-center justify-center text-[13px] font-semibold shrink-0 overflow-hidden">
                            {user?.profilePicture
                                ? <img src={user.profilePicture} alt={user?.name || 'User'} className="w-full h-full object-cover" />
                                : getInitials(user?.name)}
                        </span>
                        <div className="hidden xl:block text-left leading-tight">
                            <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100 truncate max-w-[140px]">
                                {user?.name || 'User'}
                            </p>
                            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 font-mono">{user?.phone}</p>
                        </div>
                        <IconChevronDown size={15} stroke={1.8} className="shrink-0 text-zinc-400" />
                    </button>
                )}
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-60 max-h-[85vh] overflow-y-auto font-hind">
                {/* 1. Buy credit (ক্রেডিট কিনুন) — pinned to the top as a prominent button.
                    Non-admin owners only (operators spend the owner's credit). */}
                {user?.role !== 'admin' && !isOperator && (
                    <DropdownMenuItem
                        className="cursor-pointer p-0 focus:bg-transparent"
                        onClick={() => navigate(buyCreditPath)}
                    >
                        <span className="font-hind relative mx-1 my-1 flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-amber-400 px-3 py-2 text-white shadow-sm transition-opacity hover:opacity-90">
                            <GlossShine />
                            <IconShoppingCart size={16} className="relative shrink-0" />
                            <span className="relative text-sm font-bold">{t('payments.buyTitle')}</span>
                        </span>
                    </DropdownMenuItem>
                )}

                {/* 2. Credit usage (ক্রেডিট খরচ) — shows the current credit as a badge */}
                {showCredit && (
                    <DropdownMenuItem
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => navigate('/settings?tab=credits')}
                    >
                        <IconCoins size={16} className="shrink-0" />
                        <span className="flex-1 text-sm font-normal">{t('navigation.creditUsage')}</span>
                        <span
                            className={cn(
                                'ml-auto text-xs font-bold px-1.5 py-0.5 rounded font-hind-num',
                                (user?.credit ?? 0) < 10
                                    ? 'bg-red-600 text-white'
                                    : 'bg-blue-600 text-white'
                            )}
                        >
                            {creditText}
                        </span>
                    </DropdownMenuItem>
                )}

                {/* Separator before the rest of the menu — only when the credit group above
                    actually rendered, otherwise it would lead with a stray border. */}
                {user?.role !== 'admin' && !isOperator && <DropdownMenuSeparator />}

                {/* 3. Rest of the menu — Profile, Income & Expense, Manage Shop, Settings */}

                {/* Profile — hidden on onboarding page */}
                {!isOnboarding && (
                    <DropdownMenuItem
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={handleProfile}
                    >
                        <IconUser size={16} className="shrink-0" />
                        <span className="text-sm">{t('navigation.profile')}</span>
                    </DropdownMenuItem>
                )}

                {/* My Documents, Tools, Income & Expense moved to the navbar "More" menu */}

                {/* Income & Expense — user only */}
                {user?.role !== 'admin' && (
                    <DropdownMenuItem
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => navigate('/income')}
                    >
                        <IconReceipt size={16} className="shrink-0" />
                        <span className="text-sm">{t('navigation.incomeExpense')}</span>
                    </DropdownMenuItem>
                )}

                {/* Contest — owners only (operators compete under the owner's account) */}
                {user?.role !== 'admin' && !isOperator && (
                    <DropdownMenuItem
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => navigate('/contest')}
                    >
                        <IconTrophy size={16} className="shrink-0 text-amber-500" />
                        <span className="text-sm">{t('navigation.contest')}</span>
                    </DropdownMenuItem>
                )}

                {/* Settings */}
                <DropdownMenuItem
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={handleSettings}
                >
                    <IconSettings size={16} className="shrink-0" />
                    <span className="text-sm">{t('settings.title') || 'সেটিংস'}</span>
                </DropdownMenuItem>

                {/* Admin nav lives in the header + সব মেনু sidebar — not duplicated here. */}

                {/* Theme, Language & Office spaces moved to the sidebar (সব মেনু) footer */}

                {/* 4. Profile completion (প্রোফাইল তথ্য পূরণ করুন) — nudge moved to the
                    bottom, just above logout, with the bonus tag. */}
                {showProfileCompletion && (
                    <>
                        <DropdownMenuSeparator />
                        <ProfileCompletionBanner menu onClose={closeProfileNudge} />
                    </>
                )}

                <DropdownMenuSeparator />

                {/* Logout */}
                <DropdownMenuItem
                    className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                    onClick={handleLogout}
                >
                    <IconLogout size={16} className="shrink-0" />
                    <span className="text-sm">{t('navigation.logout')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>

        {/* Lifted modals/dialogs — live outside the dropdown so they persist after it closes */}
        <UserShortcutHelpModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} showTrigger={false} />
        {showOffice && (
            <OfficeSpaceDialogs
                createOpen={officeCreateOpen} setCreateOpen={setOfficeCreateOpen}
                leaveOpen={officeLeaveOpen} setLeaveOpen={setOfficeLeaveOpen}
                officeToLeave={officeToLeave} setOfficeToLeave={setOfficeToLeave}
            />
        )}
        </>
    );
}
